import { type OpRecord } from '@dl/protocol';
import {
  type FactPayload,
  FactPayloadSchema,
  type FactOwner,
  type ResolvedSegment,
  type RevealOpPayload,
  RevealOpPayloadSchema,
  type ViewerId,
  factKey,
  foldFactChainFull,
  foldPackOps,
  isGm,
  playerAuthorityPredicateIds,
  resolveSegmentAt,
  resolveVocabAlias,
  splitSegmentKey,
  segmentKey,
  uniquenessGroupValue,
  validityCoversDay,
  boundFacetPredicate,
  revealedBoundsForViewer,
  revealsForViewer,
  viewerKey,
  type FactValidity,
} from '@dl/shared';

import { type ReadOnlyStore } from './store.js';
import {
  makeVisibleFact,
  makeVisibleSegment,
  type VisibleFact,
  type VisibleSegment,
} from './visible.js';

/**
 * In-world time. Calendar-aware versions arrive with @dl/calendar; at
 * Walking Skeleton scope `real_iso` is the wall-clock ISO string of the
 * projection request. `canonical_day` is the canonical-day-count integer
 * (03-information-model.md §6) used to filter validity ranges. When
 * `canonical_day` is omitted, validity filtering is suppressed (timeless
 * read).
 */
export interface WorldTime {
  readonly real_iso: string;
  readonly canonical_day?: number;
}

/**
 * The `t` parameter accepted by `project`. `null` means "current canonical
 * now" — at substrate level this is resolved by the Workspace cursor
 * (Phase 1 calendar concern); the projection itself reads `t.canonical_day`
 * when present and otherwise applies no validity filter. Integer / number
 * shorthand is also accepted for ergonomic test use.
 *
 * The chokepoint contract is unchanged: callers may pass a `WorldTime` or
 * `null`. The chokepoint computes a `canonical_day` for the filter and a
 * `real_iso` purely for forward-compat (calendar pack will need it).
 */
export type ProjectTime = WorldTime | number | null;

interface ResolvedTime {
  /** Day-count used to filter validity ranges; `null` means no filtering. */
  day: number | null;
}

function resolveTime(t: ProjectTime, store: ReadOnlyStore): ResolvedTime {
  if (t === null) {
    // "Current canonical now". The Workspace cursor lives in Phase 1
    // calendar territory; at substrate scope we fall back to the store's
    // optional cursor accessor, else treat as no filter (timeless read).
    const day = store.currentCanonicalDay?.() ?? null;
    return { day };
  }
  if (typeof t === 'number') return { day: t };
  return { day: t.canonical_day ?? null };
}

interface LiveFact {
  subject: string;
  predicate: string;
  base: string;
  folded: ReturnType<typeof foldFactChainFull>;
  isBelief: boolean;
  ownerKey: string;
  revealKey: string;
  segmentId: string | null;
  /**
   * The validity this VIEWER may observe (ADR-0005 §7): GM-truth validity for
   * the GM, else clamped to revealed bounds. ALL downstream validity logic
   * (the day filter, segment resolution, the belief closed-check) reads this,
   * never `folded.validity`, so an unrevealed `to` keeps the value live at a
   * later cursor (the intended stale-knowledge consequence).
   */
  viewValidity: FactValidity | null;
  /**
   * Set in the day-filter pass: `true` when this entry's `viewValidity` does
   * not cover the query day. Dropped entries stay in their segment group (so
   * a group's temporal-structure count is stable) but never emit and never
   * win resolution.
   */
  dropped: boolean;
}

interface LiveSegments {
  live: LiveFact[];
  segmentGroups: Map<string, LiveFact[]>;
  isGmViewer: boolean;
  visibleKeys: Set<string> | null;
  viewerKeyStr: string;
  viewerCanSeeSegmentValue: (g: LiveFact) => boolean;
}

/**
 * Day-INDEPENDENT projection core (ADR-0005). Folds every fact chain, applies
 * the status + owner + soft-delete + null filters, owner-scopes the segment
 * groups, and clamps each non-GM viewer's bound facets — WITHOUT resolving to a
 * query day. Both `project()` (which adds a day filter + per-`t` resolution) and
 * `projectTimeline()` (which emits every revealed segment) build on this so the
 * reveal / belief / phantom-Viewer logic lives in exactly one place.
 *
 * Constructs no `VisibleFact`/`VisibleSegment` — those are minted only in the
 * two public functions below, keeping the chokepoint enforceable.
 */
function buildLiveSegments(viewer: ViewerId, store: ReadOnlyStore): LiveSegments {
  // 1. Group fact ops by (subject, predicate) chain, in HLC order.
  // 2. Collect reveal ops (separate stream) so we can filter for non-GM.
  // Each chain entry carries the ORIGINAL (pre-alias) reveal-key of the op, so a
  // non-GM fold of an alias-merged chain can keep only the viewer's revealed-
  // origin ops (#178). Un-aliased chains have one origin and fold whole.
  const factChains = new Map<string, { payload: FactPayload; originKey: string }[]>();
  const factSubjects = new Map<string, { subject: string; predicate: string }>();
  const revealOps: { subject: string; payload: RevealOpPayload }[] = [];
  // Per merged-chain origin predicates (#178 alias leak fix): the distinct
  // ORIGINAL (pre-alias) predicate reveal-keys whose fact ops folded into each
  // (possibly aliased) chain. A merged chain is visible to a non-GM only when
  // EVERY origin is revealed — an alias must NOT let a reveal authored on one
  // predicate admit a different, unrevealed predicate's content. For an
  // un-aliased chain the set is just `{ chainKey }`, so the gate is unchanged.
  const chainOrigins = new Map<string, Set<string>>();

  // Deliberate cross-vocabulary aliases (#178, ADR-0004 §8). Folded from the
  // `__pack` meta-ops in the same stream; `resolveVocabAlias` re-points an
  // aliased `from_id` to its canonical `to_id` so the two chains (and their
  // reveals) merge. Cycles / self-aliases / dangling targets are dropped
  // upstream in `foldPackOps`, so this map is always safe to walk.
  const ops = [...store.iterateOps()];
  const packDefs = foldPackOps(
    ops.map((op) => ({ predicate: op.predicate, payload: op.payload })),
  );
  const { aliases } = packDefs;
  // Re-point a predicate through the alias map, BASE-aware: an alias merges a
  // whole predicate's identity, so it must follow that predicate's segment /
  // belief sub-chains too (`<base>#seg_…`, ADR-0005 §8). We split off the
  // segment suffix, re-point the base, and recombine — so a belief authored on
  // `from_id` (`from#seg_x`) re-homes to `to#seg_x` and correctly overlays the
  // merged canon for its owner, instead of orphaning under the aliased-away base.
  const repoint = (predicate: string): string => {
    const { base, segment } = splitSegmentKey(predicate);
    const repointedBase = resolveVocabAlias(aliases, base);
    if (repointedBase === base) return predicate;
    return segment === null ? repointedBase : segmentKey(repointedBase, segment);
  };

  // Uniqueness-key resolver (ADR-0005 §2). Prefer the store's accessor (the
  // client supplies it from its full registry incl. bundled packs); fall back
  // to the homebrew `pack.define_predicate` declarations folded from THIS op
  // stream (so a server-side store with no registry still groups
  // {object}/{role} segments correctly for homebrew-defined predicates).
  const homebrewUniqKeys = new Map<string, readonly string[]>();
  for (const decl of packDefs.predicates) {
    if (decl.uniqueness_key !== undefined) homebrewUniqKeys.set(decl.id, decl.uniqueness_key);
  }
  const uniquenessKeyOf = (predicate: string): readonly string[] | undefined =>
    store.predicateUniquenessKey?.(predicate) ?? homebrewUniqKeys.get(predicate);

  for (const op of ops) {
    if (isFactOp(op)) {
      // Engine-reserved predicates (`__pack`, `__cursor_spoof`, `__cursor_visibility`,
      // `__bundle`, `__entity`, …) carry no VisibleFact and MUST never be emitted to a
      // non-GM: a `__cursor_spoof` belief is owned by the spoofed viewer, so without
      // this guard the own-belief path would surface it on the wire and reveal the
      // spoof. Spoof/visibility reach the viewer through their dedicated resolvers, not
      // as VisibleFacts. Applied to FACT ops only — reveal ops are processed (never
      // emitted), and their envelope predicate is unrelated to this namespace.
      if (op.predicate.startsWith('__')) continue;
      // Never let a single malformed or forward-versioned `fact.*` payload throw here
      // — this is the single read chokepoint, so a throw is a total read outage for
      // the whole (append-only, unrepairable) campaign. Skip unparseable ops, exactly
      // as `resolveControlledCharacters` does.
      const parsed = FactPayloadSchema.safeParse(op.payload);
      if (!parsed.success) continue;
      const payload = parsed.data;
      // Re-home the fact (and its segment/belief sub-chain) onto its alias
      // target via the base-aware `repoint` above (no-op when unaliased).
      const predicate = repoint(op.predicate);
      const key = factKey(op.subject, predicate);
      // Track the ORIGINAL (pre-alias) reveal-key of every op folded into this
      // chain — the per-proposition consent unit the gate must honour (#178).
      const originKey = factKey(op.subject, op.predicate);
      const origins = chainOrigins.get(key);
      if (origins) origins.add(originKey);
      else chainOrigins.set(key, new Set([originKey]));
      const entry = { payload, originKey };
      const chain = factChains.get(key);
      if (chain) chain.push(entry);
      else {
        factChains.set(key, [entry]);
        factSubjects.set(key, { subject: op.subject, predicate });
      }
    } else if (isRevealOp(op)) {
      const parsed = RevealOpPayloadSchema.safeParse(op.payload);
      if (!parsed.success) continue;
      const payload = parsed.data;
      // Reveals are kept on their ORIGINAL target predicate — NOT repointed onto
      // the alias target. Repointing a `from_id` reveal onto `to_id` is exactly
      // the #178 leak (it would admit unrevealed `to_id`-origin content); the
      // origin-aware `visibleKeys` below requires a reveal per contributing id.
      revealOps.push({ subject: op.subject, payload });
    }
    // Other op kinds (entity.create, future kinds) don't contribute to
    // VisibleFact at WS scope.
  }

  const isGmViewer = isGm(viewer);
  // Reveal-keys the viewer holds, on the ORIGINAL (un-aliased) predicates.
  const revealedOriginKeys = isGmViewer ? null : revealsForViewer(revealOps, viewer);
  // A (possibly alias-merged) chain is VISIBLE to a non-GM iff the viewer holds a
  // reveal for AT LEAST ONE of its origin propositions; the per-origin fold (in
  // the `live` pass) then emits only that viewer's revealed-origin value — latest
  // revealed wins (stale-knowledge, ADR-0005 §7). So a reveal on one aliased
  // predicate surfaces that predicate's value, never the co-aliased unrevealed
  // one, and never erases the viewer's own revealed value (#178). Un-aliased
  // chains have a single origin === their key, so this is the prior per-fact gate.
  // §7.2 self-authority — a `character` Viewer sees the canon facts on its OWN
  // entity whose predicate is `player_authority` (name / appearance / portrait /
  // …) WITHOUT a reveal: a PC knows its own identity, and the player edits those
  // facts directly (op-accept gate in the DO). This ONLY widens a character's
  // view to its own authority facts — it admits no other entity's facts and no
  // non-authority (GM-secret) fact on the PC, so the phantom-Viewer chokepoint
  // holds. Status is gated downstream (drafts/hypotheticals stay GM-only). Keyed
  // by the chain's display predicate; single-origin authority chains fold whole.
  const selfAuthorityKeys = new Set<string>();
  if (!isGmViewer && viewer.kind === 'character') {
    const authorityIds = playerAuthorityPredicateIds();
    for (const [key, { subject, predicate }] of factSubjects) {
      if (subject === viewer.id && authorityIds.has(predicate)) selfAuthorityKeys.add(key);
    }
  }
  const visibleKeys = isGmViewer
    ? null
    : new Set([
        ...[...chainOrigins]
          .filter(([, origins]) => [...origins].some((o) => revealedOriginKeys!.has(o)))
          .map(([chainKey]) => chainKey),
        ...selfAuthorityKeys,
      ]);
  // ADR-0005 §7 — a segment's `to`-bound is an independently-revealable FACET.
  // For a non-GM viewer we clamp the segment's validity to the close they have
  // actually been revealed: an unrevealed `to` reads as OPEN-forward (the viewer
  // never learns the reign ended → keeps the stale value at a later cursor). The
  // GM is omniscient (no clamp). Built once; consulted as the sole gate on
  // bound visibility so the phantom-Viewer chokepoint stays intact. (The `from`
  // is not a hideable facet — see `clampValidity` for why.)
  const revealedBounds = isGmViewer ? null : revealedBoundsForViewer(revealOps, viewer);
  const viewerKeyStr = viewerKey(viewer);

  // Clamp a segment's GM-truth validity to the bounds this viewer may observe
  // (ADR-0005 §7). The revealable facet is the `to` (the *close*): an unrevealed
  // `to` reads OPEN-forward, so the viewer keeps the value live past the GM-truth
  // close — the intended stale-knowledge consequence ("she fell when fact.succeed
  // stamps her `to`"). The `from` is NOT a hideable facet: it rides with the
  // segment's value (revealing "X became ruler" inherently includes "became"),
  // and clamping it open-backward would fabricate history — leaking the value
  // into days before it was true. Clamping is therefore *monotonic*: it only ever
  // widens THIS viewer's window forward, never backward, never across owners.
  // For the GM `revealedBounds` is null → identity. The segment chain key
  // (`predicate` / `predicate#seg_…`) is the bound-facet reveal target's stem
  // (`boundFacetPredicate`).
  const clampValidity = (
    subject: string,
    segmentChainKey: string,
    validity: FactValidity | null,
  ): FactValidity | null => {
    if (revealedBounds === null || validity === null) return validity;
    // `to` withheld → drop it (open-forward); `from` is always kept.
    if (validity.to === undefined) return validity;
    if (revealedBounds.has(factKey(subject, boundFacetPredicate(segmentChainKey, 'to')))) {
      return validity;
    }
    const { to: _to, ...rest } = validity;
    return Object.keys(rest).length === 0 ? null : rest;
  };

  // Pass 1 - fold each chain and keep entries that survive the status +
  // validity + soft-delete + null-value filters (the "live" facts).
  // ADR-0005 s8 - belief is the OWNER, not a key discriminator. A belief is an
  // ordinary segment chain whose folded owner.kind === 'belief'; the believer
  // is read off owner.viewer. The base it overlays comes from splitSegmentKey.
  const live: LiveFact[] = [];
  for (const [key, chain] of factChains) {
    // Fold the full chain first to learn its owner (canon vs belief).
    let folded = foldFactChainFull(chain.map((e) => e.payload));
    if (!folded) continue;
    // Per-origin visibility (ADR-0004 §8 / #178) applies ONLY to canon chains.
    // For a non-GM, an alias-MERGED canon chain (>1 origin) re-folds using ONLY
    // the ops whose origin proposition the viewer was revealed — so they see the
    // latest value they're entitled to, never a co-aliased unrevealed predicate's
    // content, and never lose their own revealed value (latest revealed wins;
    // stale-knowledge, ADR-0005 §7). Belief chains are OWNER-gated, not
    // reveal-gated, so they always fold whole — even when an alias re-homes two
    // believers' segments onto one base — keeping the phantom-Viewer overlay
    // (below) intact. Single-origin canon chains are unchanged (origins == {key}).
    if (!isGmViewer && folded.owner.kind !== 'belief' && (chainOrigins.get(key)?.size ?? 1) > 1) {
      const visible = chain.filter((e) => revealedOriginKeys!.has(e.originKey)).map((e) => e.payload);
      if (visible.length === 0) continue; // every revealed-origin hidden → invisible
      const reFolded = foldFactChainFull(visible);
      if (!reFolded) continue;
      folded = reFolded;
    }
    if (folded.soft_deleted) continue;
    if (folded.value === null) continue;
    if (!isGmViewer && folded.status !== 'canon' && folded.status !== 'pending') continue;
    const refs = factSubjects.get(key)!;
    const isBelief = folded.owner.kind === 'belief';
    const segSplit = splitSegmentKey(refs.predicate);
    const base = segSplit.base;
    const segmentId = segSplit.segment;
    const ownerKey =
      folded.owner.kind === 'belief' ? viewerKey(folded.owner.viewer) : 'canon';
    live.push({
      subject: refs.subject,
      predicate: refs.predicate,
      base,
      folded,
      isBelief,
      ownerKey,
      revealKey: key,
      segmentId,
      // Provisional: GM-truth validity. Bound-facet clamping (succession-only)
      // overwrites this below for non-GM viewers; the day filter then reads it.
      viewValidity: folded.validity,
      dropped: false,
    });
  }

  // Pass 1.5 - collapse valid-time segments, OWNER-SCOPED (ADR-0005 s4/s5/s8).
  // Group keys on the OWNER too, so canon segments and a contradicting belief's
  // segments are SEPARATE groups: they overlap in valid time legitimately
  // (different owners) and never suppress each other. Overlap is an error only
  // WITHIN one owner's segments under one key (the auditor raises that, not us).
  // Phantom-Viewer: resolution only narrows which segment emits within an
  // owner+key group and never widens visibility across owners.
  const NUL = ' ';
  const segmentGroups = new Map<string, LiveFact[]>();
  for (const e of live) {
    const uniqKey = uniquenessKeyOf(e.base);
    const groupValue = uniquenessGroupValue(uniqKey, e.folded!.value) ?? '';
    const groupKey = factKey(e.subject, e.base) + NUL + e.ownerKey + NUL + groupValue;
    const group = segmentGroups.get(groupKey);
    if (group) group.push(e);
    else segmentGroups.set(groupKey, [e]);
  }

  // ADR-0005 §7 + §111 (progressive disclosure) — bound-facet clamping is
  // SUCCESSION-ONLY, and strictly *more* granular (§113). A predicate carrying a
  // single bare-slot segment with author-set bounds is an ordinary dated fact:
  // revealing its VALUE reveals its validity (the #25 contract is preserved).
  // Bounds become independently-revealable FACETS only once the predicate has
  // *temporal structure* — a successor segment (`fact.succeed`) or an explicit
  // `seg_…` chain — i.e. exactly the situation §7 names: "a Viewer revealed an
  // open 'Aria is ruler' does NOT learn she fell when fact.succeed stamps her
  // `to`." We therefore clamp a canon segment's view-validity to the bounds this
  // non-GM viewer actually holds ONLY within a group of >1 segment (an
  // unrevealed bound reads as OPEN in that direction → stale value retained at a
  // later cursor). The GM is omniscient (`revealedBounds === null` → no clamp).
  // A viewer's OWN belief is their own perspective (its `to` is their
  // `corrected_at`, §8), never bound-clamped. Phantom-Viewer: clamping only ever
  // widens THIS viewer's own already-revealed validity window (toward open); it
  // crosses no owners and reveals no value the viewer was not already shown.
  const groupHasTemporalStructure = (group: LiveFact[]): boolean =>
    group.length > 1 || group.some((g) => g.segmentId !== null);
  if (revealedBounds !== null) {
    for (const group of segmentGroups.values()) {
      if (!groupHasTemporalStructure(group)) continue;
      for (const e of group) {
        if (e.isBelief) continue;
        e.viewValidity = clampValidity(e.subject, e.predicate, e.folded!.validity);
      }
    }
  }


  // Day-INDEPENDENT visibility gate (ADR-0005 §7, 03 §10 no-cascade): can THIS
  // viewer see this segment's VALUE at all (ignoring the query day)? The GM sees
  // every segment; a non-GM sees a canon segment iff its (subject, predicate)
  // value is revealed to them, and always sees a belief they own. `project()`
  // layers its day filter on top; `projectTimeline()` consumes this directly so
  // the export carries every revealed segment across all in-world time.
  const viewerCanSeeSegmentValue = (g: LiveFact): boolean => {
    if (isGmViewer) return true;
    if (g.isBelief) return ownsBelief(g.folded!.owner, viewer, viewerKeyStr);
    return visibleKeys!.has(g.revealKey);
  };

  return {
    live,
    segmentGroups,
    isGmViewer,
    visibleKeys,
    viewerKeyStr,
    viewerCanSeeSegmentValue,
  };
}

/**
 * THE projection function — the single chokepoint for non-GM reads
 * (02-architecture.md §4.2 invariant #1). The same module is imported by
 * the Worker (server-side player projection) and the client
 * (Preview-as-Viewer + GM identity projection).
 *
 * Filter pipeline (in order):
 *   1. status — drafts and hypotheticals are excluded for non-GM viewers
 *      ALWAYS. `canon` and `pending` are both eligible for non-GM
 *      projection (subject to the reveal filter); when a non-GM Viewer
 *      sees a `pending` fact, the resulting `VisibleFact` carries
 *      `pending: true` so the UI can render the "awaiting GM cleanup"
 *      chip (07-session-experience.md §8.1, I.A.5 / #97). GM identity
 *      projection sees all four statuses; the GM's VisibleFact also
 *      carries `pending: true` when the underlying status is `pending`
 *      so the same render path lights up in the GM HUD.
 *   2. owner — canon facts are visible to every viewer that has a reveal;
 *      belief facts project only into the perspective of their owning
 *      Viewer (never to other viewers, not even with a reveal — beliefs
 *      are not the unit a reveal admits).
 *   3. validity — if `t` resolves to a canonical day count, only facts
 *      whose `[validity.from, validity.to]` window contains that day
 *      survive; facts with no validity are timeless (always survive).
 *   4. reveal — non-GM viewers see only facts whose (subject, predicate)
 *      has a reveal targeting them.
 *
 * Cardinality is a predicate-declaration concern (03-information-model.md
 * §5); the projection itself returns one row per (subject, predicate)
 * because the op log carries one chain per (subject, predicate). When the
 * full cardinality machinery lands in II.E (PredicateRegistry-driven), the
 * "most-recent wins by HLC if overlap" rule referenced in #25 AC will be
 * applied here. At Phase-1 substrate scope, ops on the same chain already
 * fold to the chain head's value — HLC ordering is preserved by the store.
 *
 * Determinism: identical (viewer, t, store) produces an identical
 * VisibleFact[] (stable sort by subject then predicate).
 *
 * Identity note (ADR-0004 §1 / #175 / #178): predicate ids are opaque and
 * immutable, so a fact projects under exactly its own id — there is no
 * read-time canon map for *accidental* token-merging. The one exception is
 * *deliberate* cross-vocabulary unification: an explicit
 * `pack.alias_vocab { from_id, to_id }` meta-op (folded here from the same
 * op stream) re-points the aliased `from_id` at `to_id` at projection time,
 * so the two fact chains — and any reveals targeting them — merge under the
 * canonical target. The op log is never rewritten; this is a rebuildable
 * read-time view. Cycles / dangling targets fold to advisories upstream and
 * simply do not re-point here.
 */
export function project(
  viewer: ViewerId,
  t: ProjectTime,
  store: ReadOnlyStore,
): VisibleFact[] {
  const { live, segmentGroups, isGmViewer, visibleKeys, viewerKeyStr } =
    buildLiveSegments(viewer, store);
  const { day } = resolveTime(t, store);

  // Day filter (ADR-0005 §4) — applied AFTER clamping so a stale-knowledge
  // viewer (revealed-open segment, `to` withheld) keeps the value live past the
  // GM-truth close. Reads `viewValidity`, never `folded.validity`. Dropped
  // entries stay in their group (segment counts/resolution candidacy unchanged
  // in shape) but are excluded from resolution + emission below.
  for (const e of live) {
    e.dropped = day !== null && !validityCoversDay(e.viewValidity, day);
  }
  // A non-GM viewer resolves ONLY among the segments whose VALUE is revealed to
  // them (ADR-0005 §7, 03 §10 no-cascade): an unrevealed successor must not
  // participate — it would otherwise win resolution and suppress the revealed
  // predecessor, leaking the change by omission (viewer sees nothing). A belief
  // the viewer owns is always "revealed" (it is their own perspective). The GM
  // sees every segment. Phantom-Viewer: this only ever NARROWS the candidate set
  // per viewer; it never widens visibility.
  const viewerCanSeeSegment = (g: LiveFact): boolean => {
    // A non-day-covering entry is never a resolution candidate (it would never
    // win `resolveSegmentAt` anyway; excluding it keeps the candidate set the
    // same shape it had before the day filter moved past grouping).
    if (g.dropped) return false;
    if (isGmViewer) return true;
    if (g.isBelief) return ownsBelief(g.folded!.owner, viewer, viewerKeyStr);
    return visibleKeys!.has(g.revealKey);
  };
  const suppressedRevealKeys = new Set<string>();
  for (const [, group] of segmentGroups) {
    const candidates = group.filter(viewerCanSeeSegment);
    const hasSegments = candidates.length > 1 || candidates.some((g) => g.segmentId !== null);
    if (!hasSegments) continue;
    const resolved = resolveSegmentAt(
      // Resolve against the VIEWER-clamped validity (ADR-0005 §7): with the
      // `to` hidden, a closed predecessor reads as open and keeps covering
      // later days, so a stale-knowledge viewer retains the old value instead
      // of seeing the successor.
      candidates.map<ResolvedSegment>((g) => ({
        key: g.revealKey,
        segment: g.segmentId,
        value: g.folded!.value,
        validity: g.viewValidity,
      })),
      day,
    );
    for (const g of group) {
      if (!resolved || g.revealKey !== resolved.key) suppressedRevealKeys.add(g.revealKey);
    }
  }

  // (subject, base) keys where THIS non-GM viewer holds a live, covering belief
  // segment: these suppress canon on the same base (the belief overrides canon
  // in the owner's perspective). Built ONLY from beliefs the viewer owns, so
  // another Viewer's belief can never influence it (phantom invariant). A belief
  // resolved away (closed/non-covering) does NOT count - the believer falls back
  // to canon, which is exactly corrected_at-as-to (ADR-0005 s8 "until day N").
  const viewerBeliefBases = new Set<string>();
  if (!isGmViewer) {
    for (const e of live) {
      if (!e.isBelief) continue;
      if (e.dropped) continue;
      if (!ownsBelief(e.folded!.owner, viewer, viewerKeyStr)) continue;
      if (suppressedRevealKeys.has(e.revealKey)) continue;
      // "now"/timeless has no validity filter, so a CLOSED belief (a `to`) would
      // still cover "now". Treat it as expired and defer to canon; an OPEN
      // belief keeps overriding canon at "now". Reads the viewer-clamped `to`
      // (ADR-0005 §7): a belief whose `to` the viewer hasn't been revealed
      // stays open and keeps overriding.
      if (day === null && e.viewValidity?.to !== undefined) continue;
      viewerBeliefBases.add(factKey(e.subject, e.base));
    }
  }

  // Pass 2 - emit, resolving the per-viewer, per-owner belief overlay.
  const out: VisibleFact[] = [];
  for (const e of live) {
    // Day filter (post-clamp): an entry whose view-validity does not cover the
    // query day never emits. For a stale-knowledge viewer the covering segment's
    // `to` was clamped open, so it is NOT dropped here and still emits.
    if (e.dropped) continue;
    const folded = e.folded!;
    if (e.isBelief) {
      if (isGmViewer) {
        // GM is omniscient: emit each belief under its OWN raw segment key so it
        // stays a distinct row from canon.
        const pending = folded.status === 'pending';
        out.push(
          pending
            ? makeVisibleFact(e.subject, e.predicate, folded.value, { pending: true })
            : makeVisibleFact(e.subject, e.predicate, folded.value),
        );
        continue;
      } else if (!ownsBelief(folded.owner, viewer, viewerKeyStr)) {
        // PHANTOM-VIEWER INVARIANT: a belief owned by another Viewer is
        // invisible here. Non-negotiable.
        continue;
      } else if (suppressedRevealKeys.has(e.revealKey)) {
        // A belief segment not covering the query day was resolved away.
        continue;
      } else if (day === null && e.viewValidity?.to !== undefined) {
        // Closed belief at "now": expired. The believer falls back to canon.
        // Viewer-clamped `to` (ADR-0005 §7): an unrevealed `to` keeps it open.
        continue;
      } else {
        // The viewer's own belief projects under the BASE predicate over canon,
        // no reveal required - it is their perspective.
        const pending = folded.status === 'pending';
        out.push(
          pending
            ? makeVisibleFact(e.subject, e.base, folded.value, { pending: true })
            : makeVisibleFact(e.subject, e.base, folded.value),
        );
        continue;
      }
    }
    // Canon-owned. A non-GM viewer holding a live belief on this base sees the
    // belief instead (suppress canon); otherwise the reveal gate applies.
    if (!isGmViewer && viewerBeliefBases.has(factKey(e.subject, e.base))) continue;
    if (suppressedRevealKeys.has(e.revealKey)) continue;
    if (visibleKeys && !visibleKeys.has(e.revealKey)) continue;
    // Emit under the BASE predicate so all segments collapse to one row.
    const predicate = e.base;
    const pending = folded.status === 'pending';
    out.push(
      pending
        ? makeVisibleFact(e.subject, predicate, folded.value, { pending: true })
        : makeVisibleFact(e.subject, predicate, folded.value),
    );
  }

  // Stable sort: subject, then predicate.
  out.sort((a, b) => {
    if (a.subject !== b.subject) return a.subject < b.subject ? -1 : 1;
    if (a.predicate !== b.predicate) return a.predicate < b.predicate ? -1 : 1;
    return 0;
  });
  return out;
}

function ownsBelief(owner: FactOwner, viewer: ViewerId, viewerKeyStr: string): boolean {
  if (owner.kind !== 'belief') return false;
  // The phantom-Viewer invariant: a belief owned by Viewer A is invisible
  // to Viewer B. We compare by the stable viewer key (kind + id), not by
  // identity.
  if (viewerKey(owner.viewer) !== viewerKeyStr) return false;
  // Defensive: a belief owner cannot be the GM. The op-acceptance path
  // (see apps/do-workspace) is the authoritative gate; this guard is
  // belt-and-suspenders against a malformed op slipping through.
  if (viewer.kind === 'gm') return false;
  return true;
}

function isFactOp(op: OpRecord<unknown>): boolean {
  const p = op.payload as { kind?: unknown } | undefined;
  if (!p || typeof p.kind !== 'string') return false;
  return p.kind.startsWith('fact.');
}

function isRevealOp(op: OpRecord<unknown>): boolean {
  const p = op.payload as { kind?: unknown } | undefined;
  if (!p || typeof p.kind !== 'string') return false;
  return p.kind === 'reveal' || p.kind === 'unreveal';
}

/**
 * III.B.4 (#68) — Effective-Viewer set projection.
 *
 * A Player connecting to a Workspace projects through the UNION of their
 * User Viewer perspective + every Character they control via
 * `controlled_by` facts. This helper invokes the chokepoint `project()`
 * once per Viewer and dedupes by `(subject, predicate)`. On collision the
 * first Viewer's value wins; values are identical anyway because they
 * come from the same folded chain.
 *
 * The chokepoint contract is unchanged: every constructed `VisibleFact`
 * still originates inside this package (each via `project()`).
 */
export function projectForViewers(
  viewers: readonly ViewerId[],
  t: ProjectTime,
  store: ReadOnlyStore,
): VisibleFact[] {
  if (viewers.length === 0) return [];
  if (viewers.length === 1) return project(viewers[0]!, t, store);
  const byKey = new Map<string, VisibleFact>();
  for (const viewer of viewers) {
    for (const fact of project(viewer, t, store)) {
      const key = factKey(fact.subject, fact.predicate);
      if (!byKey.has(key)) byKey.set(key, fact);
    }
  }
  const out = Array.from(byKey.values());
  out.sort((a, b) => {
    if (a.subject !== b.subject) return a.subject < b.subject ? -1 : 1;
    if (a.predicate !== b.predicate) return a.predicate < b.predicate ? -1 : 1;
    return 0;
  });
  return out;
}

/**
 * Project a (possibly precision-bearing) FactValidity down to the bare
 * half-open `{ from?, to? }` an exported VisibleSegment carries. Precision
 * hints (S6) are a GM-authoring display concern, not viewer content.
 */
function toSegmentValidity(v: FactValidity | null): { from?: number; to?: number } | null {
  if (v === null) return null;
  const out: { from?: number; to?: number } = {};
  if (v.from !== undefined) out.from = v.from;
  if (v.to !== undefined) out.to = v.to;
  return out;
}

/**
 * Day-INDEPENDENT per-Viewer projection: every valid-time SEGMENT the Viewer
 * may see, across all in-world time (ADR-0005 §"Export" / S8). Where `project()`
 * collapses each `(subject, base)` to the one segment covering a query `t`, this
 * emits the WHOLE revealed timeline — the lossless basis for per-Viewer export.
 *
 * Phantom-Viewer: builds on the shared `buildLiveSegments` reveal/belief/clamp
 * core and mints `VisibleSegment`s ONLY here. A non-GM receives only canon
 * segments revealed to them, plus beliefs THEY own — the latter projected under
 * the base predicate as `owner_kind: 'canon'` with NO `owner_belief` (a believer
 * sees their truth, not "this is a belief"). The GM receives every segment;
 * beliefs carry `owner_kind: 'belief'` + the believer's key. Bounds are already
 * viewer-clamped, so an un-revealed `to` reads open-ended (no leak).
 */
export function projectTimeline(viewer: ViewerId, store: ReadOnlyStore): VisibleSegment[] {
  const { live, isGmViewer, visibleKeys, viewerKeyStr } = buildLiveSegments(viewer, store);
  const out: VisibleSegment[] = [];
  for (const e of live) {
    const folded = e.folded!;
    const pending = folded.status === 'pending';
    if (e.isBelief) {
      if (isGmViewer) {
        out.push(
          makeVisibleSegment({
            subject: e.subject,
            predicate: e.base,
            value: folded.value,
            validity: toSegmentValidity(e.viewValidity),
            pending,
            owner_kind: 'belief',
            owner_belief: e.ownerKey,
          }),
        );
      } else if (ownsBelief(folded.owner, viewer, viewerKeyStr)) {
        // The viewer's OWN belief is their truth — emit under the base as canon.
        out.push(
          makeVisibleSegment({
            subject: e.subject,
            predicate: e.base,
            value: folded.value,
            validity: toSegmentValidity(e.viewValidity),
            pending,
            owner_kind: 'canon',
          }),
        );
      }
      // Another Viewer's belief is invisible here (phantom-Viewer). Skip.
      continue;
    }
    // Canon segment: a non-GM sees it only if its value facet is revealed.
    if (!isGmViewer && !visibleKeys!.has(e.revealKey)) continue;
    out.push(
      makeVisibleSegment({
        subject: e.subject,
        predicate: e.base,
        value: folded.value,
        validity: toSegmentValidity(e.viewValidity),
        pending,
        owner_kind: 'canon',
      }),
    );
  }
  // Deterministic order: subject, then base predicate, then segment start day.
  out.sort((a, b) => {
    if (a.subject !== b.subject) return a.subject < b.subject ? -1 : 1;
    if (a.predicate !== b.predicate) return a.predicate < b.predicate ? -1 : 1;
    const af = a.validity?.from ?? Number.NEGATIVE_INFINITY;
    const bf = b.validity?.from ?? Number.NEGATIVE_INFINITY;
    return af - bf;
  });
  return out;
}
